/**
 * MakeoverBeforeAfterSection — illustrative side-by-side demo showing
 * how the same experience reads with and without the makeover.
 *
 * Two comparison blocks: LinkedIn headline, Résumé experience bullet.
 * Mobile stacks the "Before" above the "After"; desktop keeps them
 * side-by-side.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowDown, Circle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  fadeUp,
  kicker,
  displaySection,
  sectionWrap,
  sectionInner,
} from './shared';

const COMPARISONS = [
  {
    label: 'LinkedIn headline',
    before:
      'Marketing Manager | Digital Marketing | Social Media | Looking for Opportunities',
    after:
      'B2B Marketing Manager | Helping brands build visibility and generate qualified leads through content, LinkedIn, and digital campaigns',
    testid: 'compare-headline',
  },
  {
    label: 'Résumé experience',
    before: 'Responsible for email marketing campaigns and lead generation.',
    after:
      'Planned and managed outbound email campaigns via HubSpot, Apollo and the Zerobounce stack, using audience segmentation and follow-up sequences to support qualified lead generation.',
    testid: 'compare-experience',
  },
];

const scrollToPricing = () => {
  const el =
    document.querySelector('[data-testid="home-hero-checkout-card"]') ||
    document.getElementById('pricing');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const ComparisonRow = ({ c }) => (
  <motion.div
    {...fadeUp}
    className="rounded-3xl border border-border/70 bg-card/80 backdrop-blur overflow-hidden"
    data-testid={c.testid}
  >
    <div className="px-6 py-4 border-b border-border/70 bg-muted/30">
      <p className="font-mono-edge text-[10.5px] tracking-[0.26em] uppercase text-muted-foreground">
        {c.label}
      </p>
    </div>
    <div className="grid md:grid-cols-2 gap-0">
      {/* Before */}
      <div className="p-6 border-b md:border-b-0 md:border-r border-border/70">
        <div className="flex items-center gap-2 mb-3">
          <Badge
            variant="secondary"
            className="rounded-full text-[10px] font-mono-edge tracking-[0.2em] uppercase px-2.5 py-0.5"
          >
            Before
          </Badge>
          <Circle
            size={6}
            className="text-rose-500 fill-rose-500"
            aria-hidden="true"
          />
        </div>
        <p
          className="text-[14px] text-foreground/70 leading-relaxed line-through decoration-rose-400/70 decoration-[1.5px]"
          data-testid={`${c.testid}-before`}
        >
          {c.before}
        </p>
      </div>
      {/* After */}
      <div className="p-6 bg-primary/[0.03]">
        <div className="flex items-center gap-2 mb-3">
          <Badge className="rounded-full text-[10px] font-mono-edge tracking-[0.2em] uppercase px-2.5 py-0.5">
            After
          </Badge>
          <Circle
            size={6}
            className="text-emerald-500 fill-emerald-500"
            aria-hidden="true"
          />
        </div>
        <p
          className="text-[14px] text-foreground leading-relaxed font-medium"
          data-testid={`${c.testid}-after`}
        >
          {c.after}
        </p>
      </div>
    </div>
  </motion.div>
);

export const MakeoverBeforeAfterSection = () => (
  <section
    className={sectionWrap}
    aria-label="Before and after a profile makeover"
    data-testid="makeover-before-after-section"
  >
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="max-w-3xl">
        <p className={kicker}>Before &amp; after</p>
        <h2
          className={`${displaySection} mt-4 text-3xl sm:text-4xl lg:text-5xl`}
          data-testid="before-after-headline"
        >
          Same experience.{' '}
          <span className="text-primary">A clearer reason to consider you.</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
          A stronger profile makes your value specific. Here&rsquo;s an
          illustrative example.
        </p>
      </motion.div>

      {/* Mock LinkedIn profile frame — kept lightweight and purely
          decorative so it doesn't distract from the copy inside. */}
      <motion.div
        {...fadeUp}
        className="mt-12 mx-auto max-w-3xl rounded-3xl border border-border/70 bg-card/90 overflow-hidden shadow-[0_30px_80px_-50px_hsl(220_40%_2%/0.3)]"
        data-testid="before-after-profile-mock"
      >
        <div
          className="h-20 bg-gradient-to-br from-sky-300/50 to-indigo-400/40"
          aria-hidden="true"
        />
        <div className="px-6 pb-6 -mt-10 flex items-start gap-4">
          <Avatar className="h-20 w-20 ring-4 ring-background shadow-md">
            <AvatarFallback className="bg-primary/15 text-primary text-xl font-semibold">
              AS
            </AvatarFallback>
          </Avatar>
          <div className="pt-10">
            <p className="text-lg font-semibold tracking-[-0.01em]">
              Ananya Sharma
            </p>
            <p className="text-[12px] text-muted-foreground">
              Mumbai, India &middot; Marketing &amp; Growth
            </p>
          </div>
        </div>
        <div className="px-6 pb-6 space-y-5">
          {COMPARISONS.map((c) => (
            <ComparisonRow key={c.testid} c={c} />
          ))}
        </div>
      </motion.div>

      <motion.p
        {...fadeUp}
        className="mt-8 max-w-2xl text-[13.5px] text-muted-foreground leading-relaxed"
      >
        Your makeover builds on your actual experience. Add verified results
        and numbers wherever you have them.
      </motion.p>

      <div className="mt-8">
        <Button
          size="lg"
          onClick={scrollToPricing}
          className="h-12 rounded-full px-7 text-[14.5px]"
          data-testid="before-after-cta"
        >
          Make my experience stand out
          <ArrowRight size={16} className="ml-2" aria-hidden="true" />
        </Button>
      </div>
    </div>
  </section>
);

export default MakeoverBeforeAfterSection;
